const REVEAL_CLASS = 'fade-reveal';
const VISIBLE_CLASS = 'is-visible';
const SECTION_SELECTOR = '.shopify-section';
const REVEAL_TARGETS_SELECTOR = [
  '.section > *',
  '.section-content-wrapper > *',
  '.section-resource-list__header > *',
  '.section-resource-list__content > *',
  '.product-grid > *',
].join(', ');
const VALUES_LIST_TARGETS_SELECTOR = '[data-values-scroll-reveal] .group-block-content > .group-block';
const HIDDEN_TAGS = new Set(['SCRIPT', 'STYLE', 'LINK', 'NOSCRIPT']);
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * @param {Element} element
 */
const isRevealableElement = (element) => {
  if (!(element instanceof HTMLElement)) return false;
  if (HIDDEN_TAGS.has(element.tagName)) return false;
  if (element.hidden) return false;
  if (element.matches('.custom-section-background, .section-background, .background-image-container')) return false;
  if (element.closest('header, .header, .header-section')) return false;
  if (element.closest('.press-section__quotes, .press-section__quote')) return false;
  return true;
};

/**
 * @param {Element} element
 */
const revealElement = (element) => {
  if (!(element instanceof HTMLElement)) return;
  element.classList.add(VISIBLE_CLASS);
};

/**
 * @param {Element} element
 */
const isInViewOnLoad = (element) => {
  if (!(element instanceof HTMLElement)) return false;

  const rect = element.getBoundingClientRect();
  const fold = window.innerHeight * 0.9;
  return rect.top < fold;
};

const canObserve = 'IntersectionObserver' in window;
const observer = canObserve
  ? new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          revealElement(entry.target);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.15,
        rootMargin: '0px 0px -8% 0px',
      }
    )
  : null;

/**
 * @param {HTMLElement} element
 * @param {number} delayMs
 */
const prepareRevealTarget = (element, delayMs = 0) => {
  if (!isRevealableElement(element)) return false;
  if (element.classList.contains(REVEAL_CLASS)) return false;

  element.classList.add(REVEAL_CLASS);
  element.style.setProperty('--fade-reveal-delay', `${delayMs}ms`);

  if (prefersReducedMotion || !observer || isInViewOnLoad(element)) {
    revealElement(element);
    return true;
  }

  observer.observe(element);
  return true;
};

/**
 * @param {HTMLElement} section
 */
const setupValuesListReveals = (section) => {
  const containers = section.querySelectorAll('[data-values-scroll-reveal]');
  if (containers.length === 0) return;

  for (const container of containers) {
    if (!(container instanceof HTMLElement)) continue;

    const revealTargets = container.querySelectorAll(VALUES_LIST_TARGETS_SELECTOR);
    for (const revealTarget of revealTargets) {
      if (!(revealTarget instanceof HTMLElement)) continue;
      revealTarget.classList.add('fade-reveal--values-list');
      prepareRevealTarget(revealTarget, 0);
    }
  }
};

/**
 * @param {HTMLElement} section
 */
const setupSectionReveals = (section) => {
  setupValuesListReveals(section);

  const revealTargets = section.querySelectorAll(REVEAL_TARGETS_SELECTOR);
  if (revealTargets.length === 0) return;

  let revealIndex = 0;

  for (const revealTarget of revealTargets) {
    if (!(revealTarget instanceof HTMLElement)) continue;
    if (revealTarget.hasAttribute('data-values-scroll-reveal')) continue;

    const delayMs = Math.min(revealIndex * 45, 180);
    if (prepareRevealTarget(revealTarget, delayMs)) {
      revealIndex += 1;
    }
  }
};

/**
 * @param {ParentNode} root
 */
const initializeFadeReveals = (root = document) => {
  const sections = root.querySelectorAll(SECTION_SELECTOR);
  if (sections.length === 0) return;

  for (const section of sections) {
    if (!(section instanceof HTMLElement)) continue;
    setupSectionReveals(section);
  }
};

initializeFadeReveals();
document.addEventListener('shopify:section:load', () => initializeFadeReveals());

const mutationObserver = new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (!(node instanceof HTMLElement)) continue;

      if (node.matches(SECTION_SELECTOR)) {
        initializeFadeReveals(node.parentElement ?? document);
        continue;
      }

      const nestedSection = node.querySelector(SECTION_SELECTOR);
      if (!nestedSection) continue;
      initializeFadeReveals(node);
    }
  }
});

mutationObserver.observe(document.body, { childList: true, subtree: true });
